import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./LoginGoogle.css"; // Import the CSS file for styling
import logo from "./images/logo_AIContent.png"; // Import the logo image
import { FaGoogle, FaEye, FaEyeSlash, FaEnvelope } from "react-icons/fa"; // Import icons
import { getDatabase, ref, set, get } from "firebase/database";
import { app } from "./firebase/config"; // Import app từ config
import { getAuth, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { trackEvent, trackCompleteRegistration } from "./utils/metaPixel";
import { captureFacebookAdParams, getStoredAdParams } from "./utils/adTracking";
import { verifyEmailCredentials } from "./services/emailAuthService";
import { validateEmailFormat } from "./utils/emailUserUtils";
import { useAuth } from "./contexts/AuthContext";

const database = getDatabase(app);
const auth = getAuth(app);
const provider = new GoogleAuthProvider();

// Hàm lưu thông tin người dùng vào database (Google users)
// Hàm lưu thông tin người dùng vào database (Google users)
const saveUserToDatabase = async (user, agreedToTerms = false) => {
  try {
    console.log("🔄 Bắt đầu lưu thông tin người dùng vào database (Optimized)...");
    const db = getDatabase();
    
    // OPTIMIZED: Check existence using a small field (createdAt) instead of fetching the whole user
    const createdAtRef = ref(db, `users/${user.uid}/createdAt`);
    const createdAtSnapshot = await get(createdAtRef);

    if (!createdAtSnapshot.exists()) {
      // Người dùng mới - tạo với các thông tin cơ bản
      const userRef = ref(db, `users/${user.uid}`);
      const userData = {
        userID: user.uid,
        createdAt: Date.now(),
        dailyPostCount: {},
        profile: {
          displayName: user.displayName || null,
          email: user.email || null,
          photoURL: user.photoURL || null,
          authProvider: "google",
        },
        termsAgreed: agreedToTerms ? {
          timestamp: Date.now(),
          version: "1.0",
        } : null,
      };

      // Lưu thông tin người dùng mới vào database
      await set(userRef, userData);
      
      // OPTIMIZED: Also save to user_profiles
      try {
        const userProfileData = {
          id: user.uid,
          createdAt: userData.createdAt,
          profile: userData.profile,
          emailAuth: null,
          membership: null,
          membershipExpiry: null,
          isTrial: null,
          points: null,
          stats: { postCount: 0, createdPostCount: 0 }
        };
        await set(ref(db, `user_profiles/${user.uid}`), userProfileData);
      } catch (e) {
        console.error("Error creating user_profile:", e);
      }

      console.log("✅ Đã lưu thông tin người dùng mới vào database:", user.uid);
      return { success: true, isNewUser: true };
    } else {
      // Người dùng đã tồn tại - cập nhật thông tin profile nếu cần
      // OPTIMIZED: Fetch only the profile to check for updates
      const profileRef = ref(db, `users/${user.uid}/profile`);
      const profileSnapshot = await get(profileRef);
      const profile = profileSnapshot.val() || {};

      // Chỉ cập nhật thông tin chưa có
      const updatedProfile = {
        ...profile,
        displayName: profile.displayName || user.displayName || null,
        email: profile.email || user.email || null,
        photoURL: profile.photoURL || user.photoURL || null,
        authProvider: profile.authProvider || "google",
      };

      // Cập nhật thông tin profile
      await set(ref(db, `users/${user.uid}/profile`), updatedProfile);
      
      // OPTIMIZED: Also update user_profiles
      try {
        await set(ref(db, `user_profiles/${user.uid}/profile`), updatedProfile);
      } catch (e) {
        console.error("Error updating user_profile:", e);
      }

      // Update terms agreement if provided and not already set
      if (agreedToTerms) {
        const termsRef = ref(db, `users/${user.uid}/termsAgreed`);
        const termsSnapshot = await get(termsRef);
        if (!termsSnapshot.exists()) {
          await set(termsRef, {
            timestamp: Date.now(),
            version: "1.0",
          });
        }
      }

      console.log("✅ Đã cập nhật thông tin profile người dùng:", user.uid);
      return { success: true, isNewUser: false };
    }
  } catch (error) {
    console.error("❌ Lỗi khi lưu thông tin người dùng vào database:", error);
    // Trả về lỗi để có thể xử lý ở phía gọi hàm
    return { success: false, error };
  }
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    loginWithEmail,
    loginWithGoogle,
    isAuthenticated,
    isLoading: authLoading,
  } = useAuth();

  // Local state
  const [isLoading, setIsLoading] = useState(false);
  const [adParams, setAdParams] = useState({});

  // Login method state
  const [loginMethod, setLoginMethod] = useState("google"); // 'google' or 'email'

  // Add a separate loading state for email login to avoid conflicts
  const [isEmailLoading, setIsEmailLoading] = useState(false);

  // Flag to prevent clearing errors immediately after login failure
  const [justFailedLogin, setJustFailedLogin] = useState(false);

  // Email/Password form state
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  // Error state
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    general: "",
  });

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [isManualLogin, setIsManualLogin] = useState(false);
  const [isGooglePopupOpen, setIsGooglePopupOpen] = useState(false);

  // Terms agreement state
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Safety timeout to reset loading states if they get stuck
  useEffect(() => {
    if (isEmailLoading || isLoading) {
      const timeout = setTimeout(() => {
        console.log("⚠️ Safety timeout: Resetting stuck loading states");
        setIsEmailLoading(false);
        setIsLoading(false);
        setIsManualLogin(false);
      }, 10000); // 10 seconds timeout

      return () => clearTimeout(timeout);
    }
  }, [isEmailLoading, isLoading]);

  // Debug effect to track errors state changes
  useEffect(() => {
    console.log("🔍 Errors state changed:", errors);
  }, [errors]);

  // Restore error from sessionStorage on mount
  useEffect(() => {
    const storedError = sessionStorage.getItem("loginError");
    if (storedError) {
      console.log("🔄 Restoring error from sessionStorage:", storedError);
      setErrors((prev) => ({
        ...prev,
        general: storedError,
      }));
      setJustFailedLogin(true);

      // Clear after showing
      setTimeout(() => {
        setJustFailedLogin(false);
        sessionStorage.removeItem("loginError");
      }, 5000);
    }
  }, []);

  // Lấy returnUrl từ URL search params
  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get("returnUrl") || "/normal-post";

  // Thêm class login-page vào body khi component mount và xóa khi unmount
  useEffect(() => {
    document.body.classList.add("login-page");
    return () => {
      document.body.classList.remove("login-page");
    };
  }, []);

  // Check if user is already authenticated and redirect
  useEffect(() => {
    console.log("🔍 Login useEffect - Auth state:", {
      isAuthenticated: isAuthenticated(),
      authLoading,
      isLoading,
      isEmailLoading,
      userId: localStorage.getItem("userId"),
      authMethod: localStorage.getItem("authMethod"),
      userAccessToken: !!localStorage.getItem("userAccessToken"),
    });

    if (isAuthenticated() && !authLoading) {
      const redirectUrl = searchParams.get("returnUrl") || "/normal-post";
      console.log("🔄 User authenticated, redirecting to:", redirectUrl);
      navigate(redirectUrl, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, searchParams]);

  useEffect(() => {
    // Capture ad parameters
    handleCaptureAdParams();

    // Xóa các trạng thái đăng nhập cũ ở localStorage nếu không đang đăng nhập thủ công
    if (!isManualLogin) {
      localStorage.removeItem("redirecting");
      localStorage.removeItem("returnUrl");
    }
  }, [location, isManualLogin]);

  // Capture ad parameters from URL using our utility function
  const handleCaptureAdParams = () => {
    const params = captureFacebookAdParams();
    if (params) {
      setAdParams(params);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const previousValue = formData[name];

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Only clear errors if the value actually changed (not just on re-render)
    // and not immediately after a failed login
    if (value !== previousValue && !justFailedLogin) {
      // Clear specific error when user starts typing
      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: "",
        }));
      }

      // Clear general error only when user actually types
      if (errors.general) {
        setErrors((prev) => ({
          ...prev,
          general: "",
        }));
      }
    }

    // Reset the flag after user starts typing
    if (value !== previousValue && justFailedLogin) {
      setTimeout(() => setJustFailedLogin(false), 100);
    }
  };

  // Validate email/password form
  const validateForm = () => {
    const newErrors = {};

    // Validate email
    const emailValidation = validateEmailFormat(formData.email);
    if (!emailValidation.isValid) {
      newErrors.email = emailValidation.error;
    }

    // Validate password
    if (!formData.password) {
      newErrors.password = "Mật khẩu là bắt buộc";
    } else if (formData.password.length < 6) {
      newErrors.password = "Mật khẩu phải có ít nhất 6 ký tự";
    }

    // Validate terms agreement
    if (!agreedToTerms) {
      newErrors.general = "Bạn phải đồng ý với Điều khoản dịch vụ và Chính sách bảo mật để tiếp tục";
    }

    console.log("🔍 Form validation result:", {
      hasErrors: Object.keys(newErrors).length > 0,
      errors: newErrors,
      formData: {
        email: formData.email,
        passwordLength: formData.password.length,
      },
      agreedToTerms,
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle email/password login
  const handleEmailLogin = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setErrors({ email: "", password: "", general: "" });

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setIsEmailLoading(true);
    setIsManualLogin(true);

    // Lưu trữ returnUrl để sử dụng sau khi đăng nhập thành công
    localStorage.setItem("returnUrl", returnUrl);

    // Track email login attempt
    trackEvent("EmailLoginAttempt", {
      button_location: "login_page",
      email: formData.email,
    });

    console.log("🔄 Starting email login process for:", formData.email);

    try {
      // Use AuthContext for email login
      const result = await loginWithEmail(formData.email, formData.password);

      if (result.success && result.user) {
        // Get stored ad parameters
        const adParams = getStoredAdParams() || {};

        // Track successful login with Meta Pixel
        trackCompleteRegistration({
          content_name: "email_login",
          status: "success",
          user_id: result.user.uid,
          ...adParams,
        });

        // Track ad conversion if we have fbclid
        if (adParams.fbclid) {
          console.log("📊 Tracking ad conversion for email login");
          trackEvent("AdConversion", {
            content_name: "email_login",
            user_id: result.user.uid,
            ...adParams,
          });
        }

        // User will be redirected by useEffect when authentication state updates
        console.log(
          "✅ Email login successful, authentication state will trigger redirect"
        );

        // Small delay to ensure state is updated
        setTimeout(() => {
          if (isAuthenticated()) {
            const redirectUrl = returnUrl || "/normal-post";
            console.log("🔄 Delayed redirect after email login:", redirectUrl);
            navigate(redirectUrl, { replace: true });
          } else {
            console.log(
              "❌ User not authenticated after email login, checking state..."
            );
            console.log("Auth state:", {
              isAuthenticated: isAuthenticated(),
              userId: localStorage.getItem("userId"),
              authMethod: localStorage.getItem("authMethod"),
              userAccessToken: !!localStorage.getItem("userAccessToken"),
            });
          }
        }, 100);
      } else {
        // Login failed - handle specific error cases
        console.log("❌ Email login failed:", result.error);

        // Determine if it's a password error or email error
        const errorMessage = result.error || "Login failed. Please try again.";

        // Set appropriate error based on the error message
        let errorToSet;
        if (errorMessage.includes("Invalid email or password")) {
          errorToSet = {
            email: "",
            password: "",
            general: "Email hoặc mật khẩu không đúng. Vui lòng kiểm tra lại.",
          };
        } else if (errorMessage.includes("Account is disabled")) {
          errorToSet = {
            email: "",
            password: "",
            general:
              "Tài khoản đã bị vô hiệu hóa. Vui lòng liên hệ quản trị viên.",
          };
        } else {
          errorToSet = {
            email: "",
            password: "",
            general: errorMessage,
          };
        }

        console.log("🔄 Setting error state:", errorToSet);
        setJustFailedLogin(true);
        setErrors(errorToSet);

        // Also store error in sessionStorage as backup
        sessionStorage.setItem("loginError", errorToSet.general);

        // Clear the flag after a delay to allow user to see the error
        setTimeout(() => {
          setJustFailedLogin(false);
          sessionStorage.removeItem("loginError");
        }, 5000);

        // Track failed login
        trackEvent("EmailLoginFailed", {
          button_location: "login_page",
          error: result.error,
        });

        // Force a small delay to ensure AuthContext loading state is reset
        setTimeout(() => {
          console.log("🔍 Auth state after failed login:", {
            authLoading,
            isAuthenticated: isAuthenticated(),
            userId: localStorage.getItem("userId"),
            authMethod: localStorage.getItem("authMethod"),
          });
        }, 100);

        // Prevent any navigation by ensuring we stay on login page
        if (window.location.pathname !== "/login") {
          console.log("🔄 Ensuring we stay on login page after failed login");
          window.history.replaceState(null, "", "/login");
        }
      }
    } catch (error) {
      console.error("❌ Email login error:", error);
      // Immediately reset loading states on error
      setIsLoading(false);
      setIsEmailLoading(false);
      setIsManualLogin(false);

      setErrors({
        email: "",
        password: "",
        general: "Đã xảy ra lỗi. Vui lòng thử lại.",
      });
    } finally {
      // Ensure all loading states are reset
      setIsLoading(false);
      setIsEmailLoading(false);
      setIsManualLogin(false);
      console.log("🔄 Email login process completed, loading states reset");

      // Additional safety: Force reset loading states after a short delay
      setTimeout(() => {
        setIsLoading(false);
        setIsEmailLoading(false);
        setIsManualLogin(false);
        console.log("🔄 Safety reset of loading states completed");
      }, 100);
    }
  };

  // Handle Google login (existing functionality)
  const handleGoogleLogin = async () => {
    // Validate terms agreement first
    if (!agreedToTerms) {
      setErrors({
        email: "",
        password: "",
        general: "Bạn phải đồng ý với Điều khoản dịch vụ và Chính sách bảo mật để tiếp tục",
      });
      return;
    }

    // Đánh dấu đây là đăng nhập thủ công
    setIsManualLogin(true);
    // Đánh dấu popup Google đang mở
    setIsGooglePopupOpen(true);

    // Lưu trữ returnUrl để sử dụng sau khi đăng nhập thành công
    localStorage.setItem("returnUrl", returnUrl);

    // Track the click event with Meta Pixel
    trackEvent("GoogleLoginClick", { button_location: "login_page" });

    setIsLoading(true);

    // Thêm giá trị prompt cho GoogleAuthProvider để luôn hiển thị popup chọn tài khoản
    provider.setCustomParameters({
      prompt: "select_account",
    });

    try {
      const result = await signInWithPopup(auth, provider);

      // Đánh dấu popup Google đã đóng
      setIsGooglePopupOpen(false);

      // User signed in
      const user = result.user;
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const token = credential.accessToken;

      // Store auth token in localStorage
      localStorage.setItem("userAccessToken", token);

      // No expiry for Google tokens in the same way as FB tokens, but we'll set a default
      const oneDay = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      localStorage.setItem("userAccessTokenExpiry", Date.now() + oneDay);

      // Get stored ad parameters using our utility function
      const adParams = getStoredAdParams() || {};

      // Track successful login with Meta Pixel with ad parameters
      trackCompleteRegistration({
        content_name: "google_login",
        status: "success",
        user_id: user.uid,
        ...adParams, // Include ad parameters in tracking
      });

      // Lưu thông tin profile và userID vào realtime database
      // và thực hiện các tác vụ khác sau khi lưu thành công
      await saveUserToDatabase(user, agreedToTerms);

      // Track ad conversion if we have fbclid
      if (adParams.fbclid) {
        console.log("📊 Tracking ad conversion for registration");
        // Track a custom event for ad conversion
        trackEvent("AdConversion", {
          content_name: "registration",
          user_id: user.uid,
          ...adParams,
        });
      }

      // Use AuthContext to handle Google login
      const authResult = await loginWithGoogle(user);

      if (authResult.success) {
        // User will be redirected by useEffect when authentication state updates
        console.log(
          "✅ Google login successful, authentication state will trigger redirect"
        );

        // Small delay to ensure state is updated
        setTimeout(() => {
          if (isAuthenticated()) {
            const redirectUrl = returnUrl || "/normal-post";
            console.log("🔄 Delayed redirect after Google login:", redirectUrl);
            navigate(redirectUrl, { replace: true });
          }
        }, 100);
      } else {
        console.error("❌ Lỗi AuthContext Google login:", authResult.error);
        // Still let useEffect handle redirect if authentication succeeded
        console.log("⚠️ AuthContext error but user may still be authenticated");

        // Try redirect anyway in case authentication succeeded
        setTimeout(() => {
          if (isAuthenticated()) {
            const redirectUrl = returnUrl || "/normal-post";
            console.log(
              "🔄 Delayed redirect after Google login (with error):",
              redirectUrl
            );
            navigate(redirectUrl, { replace: true });
          }
        }, 100);
      }
    } catch (error) {
      // Đánh dấu popup Google đã đóng
      setIsGooglePopupOpen(false);

      console.error("Lỗi đăng nhập Google:", error);

      // Nếu lỗi là do người dùng đóng popup
      if (error.code === "auth/popup-closed-by-user") {
        alert("Quá trình đăng nhập bị hủy. Vui lòng thử lại.");
      } else {
        alert("Đăng nhập Google không thành công!");
      }

      setIsLoading(false);
      setIsManualLogin(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-content">
        <div className="login-header">
          <img
            src={logo}
            alt="AIContent"
            className="login-logo mx-auto w-48 md:w-96"
          />
        </div>
        <div className="login-box">
          <h1 className="login-title">Chào mừng bạn đến với AIContent</h1>
          <p className="login-subtitle">Kết nối để tiếp tục sử dụng dịch vụ</p>

          {/* Login Method Toggle */}
          <div className="login-method-toggle mb-6">
            <div className="flex rounded-lg bg-gray-100 p-1">
              <button
                type="button"
                onClick={() => setLoginMethod("google")}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  loginMethod === "google"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <FaGoogle className="inline mr-2" />
                Google
              </button>
              <button
                type="button"
                onClick={() => setLoginMethod("email")}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  loginMethod === "email"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <FaEnvelope className="inline mr-2" />
                Email
              </button>
            </div>
          </div>

          {/* Google Login */}
          {loginMethod === "google" && (
            <>
              {/* Terms Agreement Checkbox */}
              <div className="mb-4">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    disabled={isLoading}
                  />
                  <span className="text-sm text-gray-600">
                    Tôi đồng ý với{" "}
                    <a href="/terms" target="_blank" className="text-blue-600 hover:underline">
                      Điều khoản dịch vụ
                    </a>{" "}
                    và{" "}
                    <a href="/privacy" target="_blank" className="text-blue-600 hover:underline">
                      Chính sách bảo mật
                    </a>
                    . Bằng việc sử dụng miễn phí, tôi đồng ý AIContent có quyền thu thập và sử dụng dữ liệu của tôi.
                  </span>
                </label>
              </div>

              <button
                className="login-button google-login"
                onClick={handleGoogleLogin}
                disabled={isLoading}
              >
                <FaGoogle className="google-icon" />
                <span>
                  {isLoading ? "Đang kết nối..." : "Tiếp tục với Google"}
                </span>
              </button>
            </>
          )}

          {/* Email/Password Login */}
          {loginMethod === "email" && (
            <form onSubmit={handleEmailLogin} className="space-y-4">
              {/* General Error */}
              {console.log("🔍 Rendering errors.general:", errors.general)}
              {(errors.general || sessionStorage.getItem("loginError")) && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {errors.general || sessionStorage.getItem("loginError")}
                </div>
              )}

              {/* Email Input */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.email ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="Nhập email của bạn"
                  disabled={isLoading}
                  onFocus={() => {
                    if (errors.general) {
                      setErrors((prev) => ({ ...prev, general: "" }));
                      sessionStorage.removeItem("loginError");
                    }
                  }}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              {/* Password Input */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.password ? "border-red-300" : "border-gray-300"
                    }`}
                    placeholder="Nhập mật khẩu của bạn"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    disabled={isLoading}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
              </div>

              {/* Terms Agreement Checkbox */}
              <div>
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    disabled={isLoading}
                  />
                  <span className="text-sm text-gray-600">
                    Tôi đồng ý với{" "}
                    <a href="/terms" target="_blank" className="text-blue-600 hover:underline">
                      Điều khoản dịch vụ
                    </a>{" "}
                    và{" "}
                    <a href="/privacy" target="_blank" className="text-blue-600 hover:underline">
                      Chính sách bảo mật
                    </a>
                    . Bằng việc sử dụng miễn phí, tôi đồng ý AIContent có quyền thu thập và sử dụng dữ liệu của tôi.
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="login-button email-login w-full"
                disabled={isEmailLoading || isLoading}
              >
                <FaEnvelope className="google-icon" />
                <span>
                  {isEmailLoading || isLoading
                    ? "Đang đăng nhập..."
                    : "Đăng nhập với Email"}
                </span>
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
